import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { formatDate, formatDateTime, formatMoney, formatQuantity, normalizeGuid } from '../../pricing/model/format'
import type { Snapshot } from '../api/suppliesApi'
import { selectStructureWarehouses, warehousePickOf, type StructureResult } from '../../structure/model/selectors'
import { buildAddLine, buildCreate, buildHeaderUpdate, headerConflicts, buildInvoiceFill, buildPriceFactor, buildPrices } from './commands'
import { commandKey, lineKey, ofSupply, priceKey, supplyKey } from './keys'
import {
  EMPTY_FORMS,
  EMPTY_LINE_FILTER,
  EMPTY_LIST_FILTER,
  SUPPLY_STATUSES,
  type AllocationFillResult,
  type AllocationMatrix,
  type AllocationProblem,
  type AuditEntry,
  type HeaderField,
  type MatchStatus,
  type OneCReceipt,
  type PostingAttempt,
  type PostingPreview,
  type PostingProgress,
  type PriceTypesDirectory,
  type SuppliesIntentState,
  type Supply,
  type SupplyCommand,
  type SupplyLine,
  type SupplyListItem,
  type SupplyStatus,
} from './types'
import type {
  AllocationVm,
  HeaderFieldVm,
  HistoryVm,
  KeyedCommand,
  LineCellVm,
  LinesVm,
  Option,
  PostingPhase,
  PostingVm,
  PricesVm,
  ReceiptsVm,
  StatusBadgeVm,
  SuppliesListVm,
  SupplyCreateVm,
  SupplyVm,
} from './viewModels'

/**
 * Селекторы vm поставок: (результаты endpoint-ов + намерение) → то, что видит
 * экран. RootState пакет не знает: binding приложения подаёт результаты
 * `endpoint.select(args)` и свой срез `onec.supplies`.
 */
type Result<T> = QueryResultLike<T>

const STATUS_LABELS: Readonly<Record<SupplyStatus, string>> = {
  Draft: 'Черновик',
  Imported: 'Загружена',
  Matched: 'Сопоставлена',
  Priced: 'Цены заданы',
  Allocated: 'Готова к проведению',
  Posting: 'Проводится',
  Posted: 'Проведена',
  PartiallyPosted: 'Проведена частично',
  Failed: 'Ошибка проведения',
}

const STATUS_TONES: Readonly<Record<SupplyStatus, StatusBadgeVm['tone']>> = {
  Draft: 'draft',
  Imported: 'draft',
  Matched: 'draft',
  Priced: 'draft',
  Allocated: 'ready',
  Posting: 'progress',
  Posted: 'done',
  PartiallyPosted: 'partial',
  Failed: 'failed',
}

const MATCH_LABELS: Readonly<Record<MatchStatus, string>> = {
  Matched: 'Сопоставлена',
  Ambiguous: 'Несколько кандидатов',
  New: 'Новый товар',
  NotMatched: 'Не сопоставлена',
}

const COMMAND_LABELS: Readonly<Record<SupplyCommand['kind'], string>> = {
  create: 'Создание поставки',
  update: 'Сохранение шапки',
  delete: 'Удаление поставки',
  clone: 'Копия поставки',
  'import-invoice': 'Заполнение из накладной',
  'add-line': 'Новая строка',
  'update-line': 'Правка строки',
  'delete-line': 'Удаление строки',
  'set-ignored': 'Исключение строки',
  match: 'Сопоставление',
  'override-match': 'Ручная привязка',
  'reset-match': 'Сброс привязки',
  'mark-new': 'Отметка «новый товар»',
  'price-factor': 'Коэффициент цены',
  'set-prices': 'Новые цены',
  'set-line-allocations': 'Распределение строки',
  'default-warehouse': 'Склад по умолчанию',
  'fill-all-to-one': 'Всё на один склад',
  'fill-evenly': 'Поровну по складам',
  'fill-clear': 'Очистка распределения',
  post: 'Проведение в 1С',
}

export const badgeOf = (status: SupplyStatus): StatusBadgeVm => ({ status, label: STATUS_LABELS[status], tone: STATUS_TONES[status] })

export const isEditable = (status: SupplyStatus) => status !== 'Posting' && status !== 'Posted' && status !== 'PartiallyPosted'

export const STATUS_OPTIONS: readonly Option[] = [
  { value: '', label: 'Все статусы' },
  ...SUPPLY_STATUSES.map((status) => ({ value: status, label: STATUS_LABELS[status] })),
]

function viewOf<T>(result: Result<T>, isEmpty: (data: T) => boolean = () => false): QueryView {
  return queryView(result, isEmpty)
}

/**
 * Итог массового распределения словами. Ответ «не применено» — не молчаливый успех: окно говорит, что
 * распределение НЕ изменилось (служба ответила предпросмотром).
 */
function allocatedText(kind: SupplyCommand['kind'], fill: AllocationFillResult): string {
  const warnings = fill.warnings.length > 0 ? ` · ${fill.warnings.join(' · ')}` : ''
  if (!fill.applied) return `Распределение не изменено: служба ответила предпросмотром (строк в нём: ${fill.linesAffected})${warnings}`
  if (fill.linesAffected === 0) return `Подходящих строк нет — распределение не изменилось${warnings}`
  return `${kind === 'fill-clear' ? 'Распределение очищено' : 'Распределение применено'}, строк: ${fill.linesAffected}${warnings}`
}

/** Итог команды словами — без пересказа текста службы: он показывается дословно. */
function messageOf(command: SupplyCommand): string | null {
  if (command.status !== 'succeeded') return command.error?.message ?? null
  const result = command.result
  if (result === null) return null
  switch (result.kind) {
    case 'done':
      return result.warnings.length > 0 ? result.warnings.join(' · ') : null
    case 'created':
      return `Поставка ${result.supply.number}`
    case 'filled':
      return `Файл «${result.fill.fileName}»: добавлено ${result.fill.added}, обновлено ${result.fill.updated}`
    case 'allocated':
      return allocatedText(command.kind, result.fill)
    case 'factor':
      return result.factor.applied ? `Цены пересчитаны в ${result.factor.linesAffected} строках` : 'Предпросмотр коэффициента готов'
    case 'posting':
      return `Попытка проведения №${result.posting.attemptNumber} запущена`
  }
}

export function commandView(command: SupplyCommand): KeyedCommand {
  return {
    key: command.key,
    label: COMMAND_LABELS[command.kind],
    status: command.status,
    message: messageOf(command),
    error: command.error,
    resendable: command.resendable,
  }
}

const EMPTY_COMMANDS: readonly KeyedCommand[] = []

/** Склады базы для команд — из того же ответа структуры, что и выпадающий список. */
const structureWarehouses = (result: StructureResult) =>
  result.data === undefined ? [] : selectStructureWarehouses(result.data.data)

// --- список ---------------------------------------------------------------------------------

export interface SuppliesListInput {
  resourceId: string
  list: Result<Snapshot<SupplyListItem>>
  intent: SuppliesIntentState
}

export const makeSelectSuppliesListVm = () =>
  createSelector(
    [
      (input: SuppliesListInput) => input.resourceId,
      (input: SuppliesListInput) => input.list,
      (input: SuppliesListInput) => input.intent.listFilter[input.resourceId],
    ],
    (resourceId, list, filter): SuppliesListVm => ({
      resourceId,
      filter: filter ?? EMPTY_LIST_FILTER,
      statusOptions: STATUS_OPTIONS,
      query: viewOf(list, (value) => value.items.length === 0),
      // Строки — только у готового ответа: ошибка перечитывания не показывает старое как актуальное.
      rows: list.isSuccess && list.data !== undefined
        ? list.data.items.map((item) => ({
            id: item.id,
            number: item.number,
            badge: badgeOf(item.status),
            documentDate: formatDate(item.documentDate),
            supplierName: item.withoutSupplier ? 'Без поставщика' : item.supplierName,
            supplierInvoiceNumber: item.supplierInvoiceNumber,
            warehouseName: item.warehouseName,
            linesCount: item.linesCount,
            amount: formatMoney(item.amountTotal),
          }))
        : [],
      canLoadMore: list.data !== undefined && !list.data.complete,
      observedAt: list.data === undefined ? null : formatDateTime(list.data.observedAt),
    }),
  )

// --- создание -------------------------------------------------------------------------------

export interface SupplyCreateInput {
  resourceId: string
  today: string
  warehouses: StructureResult
  intent: SuppliesIntentState
  /** Окно «Поставка из накладной»: файл из адреса окна; обычная «Новая поставка» — пусто. */
  invoiceFileId?: string
}

export const makeSelectSupplyCreateVm = () =>
  createSelector(
    [
      (input: SupplyCreateInput) => input.resourceId,
      (input: SupplyCreateInput) => input.today,
      (input: SupplyCreateInput) => input.warehouses,
      (input: SupplyCreateInput) => input.intent.createForms[input.resourceId],
      (input: SupplyCreateInput) => {
        const raw = input.invoiceFileId ?? ''
        // Неверный идентификатор в адресе — своя пустая команда, а не чужое «Новой поставки».
        const detail = raw === '' ? undefined : (normalizeGuid(raw) ?? 'invalid-file')
        return input.intent.commands[commandKey(input.resourceId, '', 'create', detail)]
      },
      (input: SupplyCreateInput) => input.invoiceFileId ?? '',
    ],
    (resourceId, today, warehouses, stored, command, invoiceFileId): SupplyCreateVm => {
      const form = stored ?? {
        documentDate: today,
        warehouseKey: '',
        withoutSupplier: false,
        supplierKey: '',
        supplierName: '',
        supplierInvoiceNumber: '',
        comment: '',
      }
      const built = buildCreate(resourceId, form, structureWarehouses(warehouses))
      const sending = command !== undefined && (command.status === 'sending' || command.status === 'unknown')
      const invoiceBuilt = invoiceFileId === '' ? null : buildInvoiceFill(invoiceFileId, 'Append')
      const invoice = invoiceBuilt === null ? null : { fileId: invoiceFileId, problem: invoiceBuilt.ok ? null : invoiceBuilt.message }
      return {
        resourceId,
        invoice,
        form,
        warehouses: warehousePickOf(warehouses),
        problem: built.ok ? null : built.message,
        // Окно создаёт одну поставку: повторное нажатие после успеха дало бы вторую — с той же формой или из того же файла
        // (на телефоне форма с активной кнопкой оставалась над «Открыть поставку», аудит 15.10, п. 6).
        canSubmit:
          built.ok &&
          warehouses.data !== undefined &&
          !sending &&
          command?.status !== 'succeeded' &&
          (invoice === null || invoice.problem === null),
        command: command === undefined ? null : commandView(command),
        created: command?.status === 'succeeded' && command.result?.kind === 'created' ? command.result.supply : null,
      }
    },
  )

// --- окно поставки --------------------------------------------------------------------------

export interface SupplyInput {
  resourceId: string
  supplyId: string
  supply: Result<OneCEnvelope<Supply>>
  warehouses: StructureResult
  intent: SuppliesIntentState
}

function serverHeader(supply: Supply): Record<HeaderField, string> {
  return {
    documentDate: supply.documentDate,
    supplierInvoiceNumber: supply.supplierInvoiceNumber ?? '',
    comment: supply.comment ?? '',
    warehouseKey: supply.warehouseRef?.remoteKey ?? '',
  }
}

export const makeSelectSupplyVm = () => {
  const selectCommands = createSelector(
    [(input: SupplyInput) => input.intent.commands, (input: SupplyInput) => input.resourceId, (input: SupplyInput) => input.supplyId],
    (commands, resourceId, supplyId) => {
      const own = Object.values(commands).filter(
        (command) => ofSupply(command.key, resourceId, supplyId) && command.kind !== 'post' && command.kind !== 'create',
      )
      return own.length === 0 ? EMPTY_COMMANDS : own.map(commandView)
    },
  )
  const selectUnsaved = createSelector(
    [(input: SupplyInput) => input.intent, (input: SupplyInput) => input.resourceId, (input: SupplyInput) => input.supplyId],
    (intent, resourceId, supplyId) => {
      const owned = (key: string) => ofSupply(key, resourceId, supplyId)
      return (
        intent.headerDrafts[supplyKey(resourceId, supplyId)] !== undefined ||
        Object.keys(intent.lineDrafts).some(owned) ||
        Object.keys(intent.allocationDrafts).some(owned) ||
        Object.entries(intent.forms[supplyKey(resourceId, supplyId)]?.prices ?? {}).length > 0 ||
        Object.values(intent.commands).some((command) => owned(command.key) && (command.status === 'sending' || command.status === 'unknown'))
      )
    },
  )
  return createSelector(
    [
      (input: SupplyInput) => input.resourceId,
      (input: SupplyInput) => input.supplyId,
      (input: SupplyInput) => input.supply,
      (input: SupplyInput) => input.warehouses,
      (input: SupplyInput) => input.intent.section[supplyKey(input.resourceId, input.supplyId)],
      (input: SupplyInput) => input.intent.headerDrafts[supplyKey(input.resourceId, input.supplyId)],
      (input: SupplyInput) => input.intent.forms[supplyKey(input.resourceId, input.supplyId)],
      (input: SupplyInput) => input.intent.commands,
      selectCommands,
      selectUnsaved,
    ],
    (resourceId, supplyId, result, warehouses, section, draft, forms, commands, ownCommands, unsaved): SupplyVm => {
      // Перечитывание (status pending) оставляет прежние данные: иначе окно на каждое сохранение строки уходило в «загрузку»
      // и собиралось заново — сворачивалась «Новая строка», терялась прокрутка (аудит 15.10, п. 7).
      const supply = result.isError ? undefined : result.data?.data
      const pick = warehousePickOf(warehouses)
      const command = (kind: SupplyCommand['kind']) => commands[commandKey(resourceId, supplyId, kind)]
      const clone = command('clone')
      const remove = command('delete')
      const fill = command('import-invoice')
      const invoice = forms ?? EMPTY_FORMS
      const invoiceBuilt = buildInvoiceFill(invoice.invoiceFileId, invoice.invoiceMode)
      let header: SupplyVm['header'] = null
      if (supply !== undefined) {
        const server = serverHeader(supply)
        const fieldOf = (field: HeaderField): HeaderFieldVm => {
          const mine = draft?.values[field]
          return { value: mine ?? server[field], server: server[field], changed: mine !== undefined && mine !== server[field] }
        }
        const fields: Record<HeaderField, HeaderFieldVm> = {
          documentDate: fieldOf('documentDate'),
          supplierInvoiceNumber: fieldOf('supplierInvoiceNumber'),
          comment: fieldOf('comment'),
          warehouseKey: fieldOf('warehouseKey'),
        }
        const built = draft === undefined ? null : buildHeaderUpdate(resourceId, draft, supply, structureWarehouses(warehouses))
        // Склад шапки виден, даже если в справочнике его уже нет: имя — то, что записано в поставке. Пока 1С не
        // ответила, «нет в справочнике» не говорится: спросить было не у кого.
        const answered = pick.query.status === 'ready'
        const known = pick.options.some((option) => option.value === server.warehouseKey)
        const savedWarehouse =
          known || server.warehouseKey === ''
            ? null
            : { value: server.warehouseKey, label: answered ? `${supply.warehouseName} (нет в справочнике)` : supply.warehouseName }
        header = {
          number: supply.number,
          badge: badgeOf(supply.status),
          supplierName: supply.withoutSupplier ? 'Без поставщика' : supply.supplierName,
          withoutSupplier: supply.withoutSupplier,
          fields,
          warehouseOptions: [{ value: '', label: 'Не выбран' }, ...(savedWarehouse === null ? [] : [savedWarehouse]), ...pick.options],
          warehousePick: pick,
          warehouseExtra: savedWarehouse,
          dirty: Object.values(fields).some((field) => field.changed),
          stale: draft !== undefined && headerConflicts(draft, server).length > 0,
          updatedBy: supply.updatedBy,
          updatedAt: formatDateTime(supply.updatedAt),
          importedFileId: supply.importedFileId,
          problem: built === null || built.ok || built.message === 'Изменений нет' ? null : built.message,
        }
      }
      const filled = fill?.status === 'succeeded' && fill.result?.kind === 'filled' ? fill.result.fill : null
      return {
        resourceId,
        supplyId,
        query: viewOf(result),
        editable: supply !== undefined && isEditable(supply.status),
        header,
        summary:
          supply === undefined
            ? null
            : {
                lines: `${supply.summary.linesTotal}${supply.summary.linesIgnored > 0 ? ` (исключено ${supply.summary.linesIgnored})` : ''}`,
                matched: `${supply.summary.linesMatched} из ${supply.summary.linesTotal}; новых ${supply.summary.linesNew}, неоднозначных ${supply.summary.linesAmbiguous}, без пары ${supply.summary.linesNotMatched}`,
                quantity: formatQuantity(supply.summary.quantityTotal),
                unallocated: formatQuantity(supply.summary.quantityUnallocated),
                amount: formatMoney(supply.summary.amountTotal),
                documents: supply.summary.documentsToCreate,
                blockers: supply.summary.blockers,
              },
        section: section ?? 'lines',
        commands: ownCommands,
        unsaved,
        cloned: clone?.status === 'succeeded' && clone.result?.kind === 'created' ? clone.result.supply : null,
        deleted: remove?.status === 'succeeded',
        invoice: { fileId: invoice.invoiceFileId, mode: invoice.invoiceMode, problem: invoiceBuilt.ok ? null : invoiceBuilt.message },
        fill:
          filled === null
            ? null
            : {
                fileName: filled.fileName,
                summary: `Строк в файле ${filled.totalRows}: добавлено ${filled.added}, обновлено ${filled.updated}, уже были ${filled.skippedExisting}, удалено ${filled.removed}`,
                skipped: filled.skipped.map((item) => ({ row: item.sourceRowNumber, name: item.name, reason: item.reason })),
              },
        configurationVersion: result.data?.configurationVersion ?? null,
      }
    },
  )
}

// --- строки ---------------------------------------------------------------------------------

export interface LinesInput {
  resourceId: string
  supplyId: string
  lines: Result<Snapshot<SupplyLine>>
  editable: boolean
  intent: SuppliesIntentState
}

const cell = (mine: string | undefined, server: string): LineCellVm => ({
  value: mine ?? server,
  server,
  changed: mine !== undefined && mine !== server,
})

/** Число в поле ввода — с точкой, как его примет сервер (InvariantCulture), без локального форматирования. */
const plain = (value: number) => String(value)

export const makeSelectLinesVm = () =>
  createSelector(
    [
      (input: LinesInput) => input.resourceId,
      (input: LinesInput) => input.supplyId,
      (input: LinesInput) => input.lines,
      (input: LinesInput) => input.editable,
      (input: LinesInput) => input.intent.lineDrafts,
      (input: LinesInput) => input.intent.lineFilter[supplyKey(input.resourceId, input.supplyId)],
      (input: LinesInput) => input.intent.forms[supplyKey(input.resourceId, input.supplyId)],
      (input: LinesInput) => input.intent.commands[commandKey(input.resourceId, input.supplyId, 'price-factor', 'preview')],
      (input: LinesInput) => input.intent.commands[commandKey(input.resourceId, input.supplyId, 'price-factor', 'apply')],
    ],
    (resourceId, supplyId, lines, editable, drafts, filter, forms, factorPreview, factorApply): LinesVm => {
      const items = lines.isError ? [] : (lines.data?.items ?? [])
      const form = forms ?? EMPTY_FORMS
      const addBuilt = buildAddLine(form.addLine)
      const factorBuilt = buildPriceFactor(form.priceFactor, true)
      const typedFactor = factorBuilt.ok ? factorBuilt.data.factor : null
      const sameFactor = (command: SupplyCommand | undefined) =>
        command?.status === 'succeeded' && command.result?.kind === 'factor' && (command.data as { factor?: number } | null)?.factor === typedFactor
      // Итог показывается, только если он про ТОТ коэффициент, что сейчас в поле.
      const shown = sameFactor(factorApply) ? factorApply : sameFactor(factorPreview) ? factorPreview : undefined
      const factor = shown?.result?.kind === 'factor' ? shown.result.factor : null
      return {
        query: viewOf(lines, (value) => value.items.length === 0),
        rows: items.map((line) => {
          const draft = drafts[lineKey(resourceId, supplyId, line.id)]
          const values = draft?.values ?? {}
          return {
            id: line.id,
            lineNumber: line.lineNumber,
            name: line.rawName ?? line.oneCName ?? 'Позиция без наименования',
            article: line.rawArticle,
            barcode: line.rawBarcode,
            unitName: line.unitName,
            oneCName: line.oneCName,
            match: {
              status: line.matchStatus,
              label: MATCH_LABELS[line.matchStatus],
              detail: line.matchSource === 'Manual' ? 'вручную' : line.matchedBy,
            },
            ignored: line.isIgnored,
            quantity: cell(values.quantity, plain(line.quantity)),
            purchasePrice: cell(values.purchasePrice, plain(line.purchasePrice)),
            amount: cell(values.amount, plain(line.amount)),
            notes: cell(values.notes, line.notes ?? ''),
            allocated: formatQuantity(line.allocatedQuantity),
            fullyAllocated: line.isFullyAllocated,
            draft:
              draft === undefined
                ? null
                : { state: draft.state, message: draft.error?.message ?? null, code: draft.error?.code ?? null },
            candidates: line.candidates.map((candidate) => ({
              productId: candidate.productId,
              name: candidate.name,
              score: `${Math.round(candidate.score * 100)} %`,
            })),
            override: form.overrideProduct[line.id.toLowerCase()] ?? '',
          }
        }),
        filter: filter ?? EMPTY_LINE_FILTER,
        editable,
        addLine: form.addLine,
        addLineProblem: addBuilt.ok ? null : addBuilt.message,
        priceFactor: form.priceFactor,
        factorProblem: form.priceFactor === '' || factorBuilt.ok ? null : factorBuilt.message,
        factorPreview:
          factor === null
            ? null
            : {
                linesAffected: factor.linesAffected,
                linesSkipped: factor.linesSkipped,
                oldAmount: formatMoney(factor.oldAmountTotal),
                newAmount: formatMoney(factor.newAmountTotal),
                applied: factor.applied,
              },
      }
    },
  )

// --- распределение --------------------------------------------------------------------------

export interface AllocationInput {
  resourceId: string
  supplyId: string
  matrix: Result<OneCEnvelope<AllocationMatrix>>
  problems: Result<OneCEnvelope<AllocationProblem[]>>
  warehouses: StructureResult
  editable: boolean
  intent: SuppliesIntentState
}

export const makeSelectAllocationVm = () =>
  createSelector(
    [
      (input: AllocationInput) => input.resourceId,
      (input: AllocationInput) => input.supplyId,
      (input: AllocationInput) => input.matrix,
      (input: AllocationInput) => input.problems,
      (input: AllocationInput) => input.warehouses,
      (input: AllocationInput) => input.editable,
      (input: AllocationInput) => input.intent.allocationDrafts,
      (input: AllocationInput) => input.intent.forms[supplyKey(input.resourceId, input.supplyId)],
    ],
    (resourceId, supplyId, matrix, problems, warehouses, editable, drafts, forms): AllocationVm => {
      const data = matrix.isSuccess ? matrix.data?.data : undefined
      const pick = warehousePickOf(warehouses)
      return {
        query: viewOf(matrix, (value) => value.data.rows.length === 0),
        columns: (data?.warehouses ?? []).map((warehouse) => ({
          key: warehouse.ref.remoteKey,
          name: warehouse.name,
          organizationName: warehouse.organizationName,
          isDefault: warehouse.isDefault,
          isMain: warehouse.isMain,
          total: formatQuantity(warehouse.quantityTotal),
        })),
        rows: (data?.rows ?? []).map((row) => {
          const draft = drafts[lineKey(resourceId, supplyId, row.lineId)]
          const cells: Record<string, LineCellVm> = {}
          for (const warehouse of data?.warehouses ?? []) {
            const key = warehouse.ref.remoteKey
            cells[key] = cell(draft?.cells[key], row.cells[key] === undefined ? '' : plain(row.cells[key]))
          }
          return {
            lineId: row.lineId,
            lineNumber: row.lineNumber,
            name: row.name ?? 'Позиция без наименования',
            quantity: formatQuantity(row.quantity),
            unallocated: formatQuantity(row.unallocated),
            balanced: row.unallocated === 0,
            ignored: row.isIgnored,
            cells,
            draft: draft === undefined ? null : { state: draft.state, message: draft.error?.message ?? null },
          }
        }),
        unallocatedTotal: formatQuantity(data?.unallocatedTotal ?? null),
        documentsToCreate: data?.documentsToCreate ?? 0,
        editable,
        problems: problems.isSuccess ? (problems.data?.data ?? []).map((problem) => ({ lineNumber: problem.lineNumber, message: problem.message })) : [],
        problemsQuery: viewOf(problems),
        fillWarehouseKey: forms?.fillWarehouseKey ?? '',
        warehouseOptions: pick.options,
        warehousePick: pick,
      }
    },
  )

// --- цены -----------------------------------------------------------------------------------

export interface PricesInput {
  resourceId: string
  supplyId: string
  lines: Result<Snapshot<SupplyLine>>
  priceTypes: Result<OneCEnvelope<PriceTypesDirectory>>
  editable: boolean
  intent: SuppliesIntentState
}

export const makeSelectPricesVm = () =>
  createSelector(
    [
      (input: PricesInput) => input.resourceId,
      (input: PricesInput) => input.lines,
      (input: PricesInput) => input.priceTypes,
      (input: PricesInput) => input.editable,
      (input: PricesInput) => input.intent.forms[supplyKey(input.resourceId, input.supplyId)]?.prices,
    ],
    (resourceId, lines, priceTypes, editable, typed): PricesVm => {
      const types = priceTypes.isSuccess ? (priceTypes.data?.data.items ?? []) : []
      const items = lines.isSuccess ? (lines.data?.items ?? []) : []
      const prices = typed ?? {}
      const built = Object.keys(prices).length === 0 ? null : buildPrices(resourceId, prices, items, types)
      const directory = priceTypes.data?.data
      return {
        query: viewOf(priceTypes, (value) => value.data.items.length === 0),
        types: types.map((type) => ({ key: type.ref.remoteKey, name: type.name })),
        typesNote: directory === undefined || directory.isReady ? null : (directory.lastMessage ?? 'Виды цен ещё не получены из 1С'),
        rows: items
          .filter((line) => !line.isIgnored)
          .map((line) => ({
            lineId: line.id,
            lineNumber: line.lineNumber,
            name: line.rawName ?? line.oneCName ?? 'Позиция без наименования',
            purchasePrice: formatMoney(line.purchasePrice),
            cells: types.map((type) => {
              const own = line.prices.find((price) => price.priceTypeRef?.remoteKey === type.ref.remoteKey)
              const key = priceKey(line.id, type.ref.remoteKey)
              return {
                key,
                current: formatMoney(own?.currentPrice ?? null),
                planned: own?.newPrice === null || own?.newPrice === undefined ? '' : plain(own.newPrice),
                typed: prices[key] ?? null,
              }
            }),
          })),
        dirty: Object.keys(prices).length > 0,
        problem: built === null || built.ok ? null : built.message,
        editable,
      }
    },
  )

// --- проведение -----------------------------------------------------------------------------

export interface PostingInput {
  resourceId: string
  supplyId: string
  supply: Result<OneCEnvelope<Supply>>
  preview: Result<OneCEnvelope<PostingPreview>>
  status: Result<OneCEnvelope<PostingProgress>>
  intent: SuppliesIntentState
  /** Номер версии клиента — в отчёт, как у донора (карточка 99). */
  clientVersion: string
}

function phaseOf(supply: Supply | undefined, command: SupplyCommand | undefined, progress: PostingProgress | undefined, canPost: boolean): PostingPhase {
  if (command?.status === 'sending') return 'sending'
  // Неизвестный исход POST — своё состояние: ни «не проведено», ни «проведено». Выясняет «Проверить статус».
  if (command?.status === 'unknown') return 'unknown'
  // Журнал саги — источник правды о ходе: закончилась — не «проводится», даже если поставка в кэше ещё старая.
  if (progress !== undefined ? progress.isRunning : supply?.status === 'Posting') return 'running'
  if (supply?.status === 'Posted') return 'posted'
  if (supply?.status === 'PartiallyPosted') return 'partial'
  if (supply?.status === 'Failed') return canPost ? 'ready' : 'failed'
  return canPost ? 'ready' : 'not-ready'
}

const PHASE_TEXT: Readonly<Record<PostingPhase, string>> = {
  'not-ready': 'не готова к проведению',
  ready: 'готова к проведению',
  sending: 'запрос проведения отправлен',
  unknown: 'исход запроса проведения неизвестен',
  running: 'проводится',
  posted: 'проведена',
  partial: 'проведена частично',
  failed: 'ошибка проведения',
}

export const makeSelectPostingVm = () =>
  createSelector(
    [
      (input: PostingInput) => input.resourceId,
      (input: PostingInput) => input.supplyId,
      (input: PostingInput) => input.supply,
      (input: PostingInput) => input.preview,
      (input: PostingInput) => input.status,
      (input: PostingInput) => input.intent.commands[commandKey(input.resourceId, input.supplyId, 'post')],
      (input: PostingInput) => input.clientVersion,
      (input: PostingInput) =>
        Object.keys(input.intent.lineDrafts).some((key) => ofSupply(key, input.resourceId, input.supplyId)) ||
        Object.keys(input.intent.allocationDrafts).some((key) => ofSupply(key, input.resourceId, input.supplyId)),
    ],
    (resourceId, supplyId, supplyResult, previewResult, statusResult, command, clientVersion, drafts): PostingVm => {
      const supply = supplyResult.data?.data
      const preview = previewResult.isSuccess ? previewResult.data?.data : undefined
      const progress = statusResult.data?.data
      const phase = phaseOf(supply, command, progress, preview?.canPost === true)
      const entries = progress?.entries ?? []
      const report = [
        `Поставка ${supply?.number ?? supplyId} — ${PHASE_TEXT[phase]}`,
        `База (rid): ${resourceId}`,
        `Статус поставки: ${supply?.statusName ?? '—'}`,
        `Клиент: ${clientVersion}`,
        command === undefined
          ? null
          : `Команда проведения: ${command.status}, операция ${command.operationId}, ключ ${command.idempotencyKey}${command.error === null ? '' : `, ${command.error.code}: ${command.error.message}${command.error.correlationId === null ? '' : ` (запрос ${command.error.correlationId})`}`}`,
        progress === undefined
          ? null
          : `Попытка ${progress.attemptNumber}: шагов ${progress.completedSteps}/${progress.totalSteps}, ошибок ${progress.failedSteps}, пропущено ${progress.skippedSteps}${progress.finalStatus === null ? '' : `, итог ${progress.finalStatus}`}`,
        ...entries
          .filter((entry) => entry.resourceErrorText !== null)
          .map(
            (entry) =>
              `${entry.stepKind} [${entry.status}]${entry.httpStatus === null ? '' : ` HTTP ${entry.httpStatus}`}: ${entry.resourceErrorText ?? ''}${entry.correlationId === null ? '' : ` (корреляция ${entry.correlationId})`}`,
          ),
        ...(progress?.documents ?? []).map(
          (document) =>
            `${document.kind} → ${document.warehouseName}: ${document.state}${document.documentNumber === null ? '' : ` №${document.documentNumber}`}${document.resourceErrorText === null ? '' : ` — ${document.resourceErrorText}`}`,
        ),
      ]
        .filter((line): line is string => line !== null)
        .join('\n')
      return {
        phase,
        previewQuery: viewOf(previewResult),
        headline: preview?.headline ?? null,
        documents: (preview?.documents ?? []).map((document) => ({
          ...document,
          quantity: formatQuantity(document.quantityTotal),
          amount: formatMoney(document.amountTotal),
        })),
        pricesToWrite: preview?.pricesToWrite ?? 0,
        warnings: preview?.warnings ?? [],
        problems: (preview?.problems ?? []).map((problem) => `Строка ${problem.lineNumber}: ${problem.message}`),
        blockers: preview?.resourceBlockers ?? [],
        canPost:
          preview?.canPost === true &&
          supply !== undefined &&
          // Повтор после частичного проведения — новая команда по свежему предпросмотру: сага пропустит сделанные шаги.
          (phase === 'ready' || phase === 'failed' || phase === 'partial') &&
          command?.status !== 'sending' &&
          command?.status !== 'unknown' &&
          // Несохранённые правки строк: предпросмотр уже не про то, что уйдёт.
          !drafts,
        draftsPending: drafts,
        command: command === undefined ? null : commandView(command),
        statusQuery: viewOf(statusResult),
        progress:
          progress === undefined
            ? null
            : {
                attemptNumber: progress.attemptNumber,
                done: progress.completedSteps,
                total: progress.totalSteps,
                failed: progress.failedSteps,
                skipped: progress.skippedSteps,
                entries,
                documents: progress.documents,
                finalStatus: progress.finalStatus,
              },
        report,
        // Опрос — только пока сага идёт и чтение статуса удаётся: ошибка опроса не повторяется каждые 1,5 с.
        poll: phase === 'running' && !statusResult.isError,
        /** Сага закончилась, а поставка в кэше ещё «Проводится» — её пора перечитать. */
        supplyOutdated: progress !== undefined && !progress.isRunning && supply?.status === 'Posting',
      }
    },
  )

// --- история --------------------------------------------------------------------------------

export interface HistoryInput {
  history: Result<Snapshot<AuditEntry>>
  log: Result<OneCEnvelope<PostingAttempt[]>>
}

export const makeSelectHistoryVm = () =>
  createSelector(
    [(input: HistoryInput) => input.history, (input: HistoryInput) => input.log],
    (history, log): HistoryVm => ({
      query: viewOf(history, (value) => value.items.length === 0),
      entries: history.isSuccess ? (history.data?.items ?? []).map((entry) => ({ ...entry, occurredAt: formatDateTime(entry.occurredAt) })) : [],
      canLoadMore: history.data !== undefined && !history.data.complete,
      logQuery: viewOf(log, (value) => value.data.length === 0),
      attempts: log.isSuccess ? (log.data?.data ?? []) : [],
    }),
  )

// --- приходные 1С ---------------------------------------------------------------------------

export interface ReceiptsInput {
  receipts: Result<Snapshot<OneCReceipt>>
}

export const makeSelectReceiptsVm = () =>
  createSelector(
    [(input: ReceiptsInput) => input.receipts],
    (receipts): ReceiptsVm => ({
      query: viewOf(receipts, (value) => value.items.length === 0),
      rows: receipts.isSuccess
        ? (receipts.data?.items ?? []).map((item) => ({ ...item, amount: formatMoney(item.sum), dateText: item.date ?? '—' }))
        : [],
      canLoadMore: receipts.data !== undefined && !receipts.data.complete,
    }),
  )
